import {
  createEffect,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  Show,
  type JSX,
} from "solid-js";
import { DumpDetail, DumpField } from "../components/DumpDetail";
import { DumpIcon } from "../components/DumpIcon";
import {
  dumpFilterQuery,
  DumpList,
  type DumpAdvancedSearchField,
  type DumpListEntry,
} from "../components/DumpList";
import type { DumpIconName } from "../generated/dump-icons";
import { apiRequest } from "../core/api";
import { currentUser } from "../core/session";
import type { ResourceChangedDetail } from "../core/realtime";

export type CrudRecord = Record<string, unknown> & {
  id?: number | string;
};

export type CrudField = {
  key: string;
  label: string;
  type?: "text" | "number" | "checkbox";
  readOnly?: boolean;
  placeholder?: string;
};

type CrudCollectionPageProps = {
  label: string;
  singular?: string;
  endpoint: string;
  icon: DumpIconName;
  fields: CrudField[];
  createDefaults?: CrudRecord;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  search?: boolean;
  advancedSearch?: boolean;
  advancedFields?: DumpAdvancedSearchField[];
  itemTitle: (item: CrudRecord) => string;
  itemDescription?: (item: CrudRecord) => string;
  itemLeftLabel?: (item: CrudRecord) => string;
  itemRightLabel?: (item: CrudRecord) => string;
  itemAvatar?: (item: CrudRecord) => string | null | undefined;
  detailEndpoint?: (item: CrudRecord) => string;
  loadDetail?: boolean;
  updateEndpoint?: (item: CrudRecord) => string;
  deleteEndpoint?: (item: CrudRecord) => string;
  createEndpoint?: string | ((item: CrudRecord) => string);
  prepareWrite?: (item: CrudRecord, creating: boolean) => CrudRecord;
  renderAfterFields?: (item: CrudRecord, reload: () => Promise<void>) => JSX.Element;
  actions?: JSX.Element;
};

export function CrudCollectionPage(props: CrudCollectionPageProps) {
  const [items, setItems] = createSignal<CrudRecord[]>([]);
  const [selected, setSelected] = createSignal<CrudRecord | null>(null);
  const [draft, setDraft] = createSignal<CrudRecord>({});
  const [creating, setCreating] = createSignal(false);
  const [loading, setLoading] = createSignal(true);
  const [saving, setSaving] = createSignal(false);
  const [search, setSearch] = createSignal("");
  const [advanced, setAdvanced] = createSignal<Record<string, string>>({});
  const [error, setError] = createSignal("");

  const loadList = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await apiRequest<
        CrudRecord[] | { items?: CrudRecord[] }
      >(props.endpoint, {
        token: currentUser()?.accessToken,
        query: {
          ...(search() ? { search: search() } : {}),
          ...dumpFilterQuery(advanced()),
        },
      });
      setItems(readItems(payload));
    } catch (reason) {
      setItems([]);
      setError(message(reason));
    } finally {
      setLoading(false);
    }
  };

  createEffect(
    on(
      [search, () => JSON.stringify(advanced()), () => props.endpoint],
      () => void loadList(),
      { defer: false },
    ),
  );
  createEffect(
    on(
      () => props.endpoint,
      () => {
        setSelected(null);
        setDraft({});
        setCreating(false);
        setError("");
        setAdvanced({});
      },
      { defer: true },
    ),
  );

  onMount(() => {
    const refresh = (event: Event) => {
      const route = (event as CustomEvent<ResourceChangedDetail>).detail.route;
      if (route.startsWith(props.endpoint)) {
        void loadList();
      }
    };
    window.addEventListener("mdt:resource-changed", refresh);
    onCleanup(() =>
      window.removeEventListener("mdt:resource-changed", refresh),
    );
  });

  const open = async (entry: DumpListEntry) => {
    const item = entry.raw as CrudRecord;
    setError("");
    setCreating(false);
    setSelected(item);
    setDraft({ ...item });
    if (!item.id || props.loadDetail === false) return;
    try {
      const detail = await apiRequest<CrudRecord>(
        props.detailEndpoint?.(item) ?? `${props.endpoint}/${item.id}`,
        { token: currentUser()?.accessToken },
      );
      setSelected(detail);
      setDraft({ ...detail });
    } catch (reason) {
      setError(message(reason));
    }
  };

  const startCreate = () => {
    const defaults = { ...(props.createDefaults ?? {}) };
    setCreating(true);
    setSelected(defaults);
    setDraft(defaults);
    setError("");
  };

  const save = async () => {
    const current = selected();
    if (!current) return;
    setSaving(true);
    setError("");
    try {
      const body = props.prepareWrite
        ? props.prepareWrite(draft(), creating())
        : withoutReadOnly(draft(), props.fields);
      const saved = await apiRequest<CrudRecord>(
        creating()
          ? typeof props.createEndpoint === "function"
            ? props.createEndpoint(draft())
            : props.createEndpoint ?? props.endpoint
          : props.updateEndpoint?.(current) ??
              `${props.endpoint}/${current.id}`,
        {
          method: creating() ? "POST" : "PATCH",
          token: currentUser()?.accessToken,
          body,
        },
      );
      setSelected(saved);
      setDraft({ ...saved });
      setCreating(false);
      await loadList();
    } catch (reason) {
      setError(message(reason));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const current = selected();
    if (!current?.id || creating()) return;
    setSaving(true);
    setError("");
    try {
      await apiRequest(
        props.deleteEndpoint?.(current) ?? `${props.endpoint}/${current.id}`,
        {
          method: "DELETE",
          token: currentUser()?.accessToken,
        },
      );
      setSelected(null);
      setDraft({});
      await loadList();
    } catch (reason) {
      setError(message(reason));
    } finally {
      setSaving(false);
    }
  };

  const back = () => {
    setSelected(null);
    setDraft({});
    setCreating(false);
    setError("");
  };

  return (
    <Show
      when={selected()}
      fallback={
        <div class="h-full">
          <Show when={error()}>
            <div class="mb-3 border border-red-500/30 bg-red-950/30 px-3 py-2 text-sm text-red-300">
              {error()}
            </div>
          </Show>
          <DumpList
            label={props.label}
            loading={loading()}
            actions={props.actions}
            search={search()}
            onSearch={props.search === false ? undefined : setSearch}
            onAdd={props.canCreate ? startCreate : undefined}
            advancedFields={
              props.advancedSearch === false || props.search === false
                ? undefined
                : props.advancedFields
            }
            advancedValues={advanced()}
            onAdvancedApply={
              props.advancedSearch === false || props.search === false
                ? undefined
                : setAdvanced
            }
            onSelect={open}
            entries={items().map((item, index) => ({
              id: primitive(item.id) ?? index,
              title: props.itemTitle(item),
              description: props.itemDescription?.(item),
              avatar: props.itemAvatar?.(item),
              icon: props.icon,
              leftLabel:
                props.itemLeftLabel?.(item) ??
                `ID: ${String(item.id ?? index)}`,
              rightLabel: props.itemRightLabel?.(item),
              raw: item,
            }))}
          />
        </div>
      }
    >
      {(active) => (
        <DumpDetail
          label={`${creating() ? "Create" : "View"} ${
            props.singular ?? singular(props.label)
          }${active().id ? ` ( #${String(active().id)} )` : ""}`}
          onBack={back}
          actions={
            <>
              <Show when={props.canDelete && !creating()}>
                <button
                  class="flex h-7 w-7 cursor-pointer items-center justify-center bg-red-gradient hover:drop-shadow-red-gradient disabled:opacity-40"
                  aria-label="Delete"
                  disabled={saving()}
                  onClick={() => void remove()}
                >
                  <DumpIcon name="tab_close" />
                </button>
              </Show>
              <Show when={props.canUpdate !== false || creating()}>
                <button
                  class="flex h-7 w-7 cursor-pointer items-center justify-center bg-green-gradient hover:drop-shadow-green-gradient disabled:opacity-40"
                  aria-label="Save"
                  disabled={saving()}
                  onClick={() => void save()}
                >
                  <DumpIcon name="icon_save_docs" />
                </button>
              </Show>
            </>
          }
        >
          <div class="flex h-full min-h-0 flex-col gap-4 p-3 lg:flex-row">
            <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
              <Show when={error()}>
                <div class="border border-red-500/30 bg-red-950/30 px-3 py-2 text-sm text-red-300">
                  {error()}
                </div>
              </Show>
              <ul class="flex min-h-11 w-full flex-col gap-3">
                <For each={props.fields}>
                  {(field) => (
                    field.type === "checkbox" ? (
                      <label class="flex items-center justify-between bg-white-11 px-3 py-2">
                        <span>{field.label}</span>
                        <input
                          type="checkbox"
                          checked={Boolean(draft()[field.key])}
                          disabled={field.readOnly}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              [field.key]: event.currentTarget.checked,
                            }))
                          }
                        />
                      </label>
                    ) : (
                      <DumpField
                        label={field.label}
                        type={field.type}
                        disabled={field.readOnly}
                        value={fieldValue(draft()[field.key])}
                        onInput={(value) =>
                          setDraft((current) => ({
                            ...current,
                            [field.key]:
                              field.type === "number"
                                ? value === ""
                                  ? null
                                  : Number(value)
                                : value,
                          }))
                        }
                      />
                    )
                  )}
                </For>
              </ul>
            </div>
            <Show when={props.renderAfterFields}>
              <aside class="w-full max-w-full overflow-y-auto pr-1 lg:max-w-[30%]">
                {props.renderAfterFields?.(draft(), async () => {
                  const item = selected();
                  if (item) {
                    await open({ id: String(item.id ?? ""), title: "", raw: item });
                  }
                })}
              </aside>
            </Show>
          </div>
        </DumpDetail>
      )}
    </Show>
  );
}

function readItems(
  payload: CrudRecord[] | { items?: CrudRecord[] },
): CrudRecord[] {
  return Array.isArray(payload)
    ? payload
    : Array.isArray(payload.items)
      ? payload.items
      : [];
}

function withoutReadOnly(item: CrudRecord, fields: CrudField[]): CrudRecord {
  const output: CrudRecord = {};
  for (const field of fields) {
    if (!field.readOnly) output[field.key] = item[field.key];
  }
  return output;
}

function fieldValue(value: unknown): string | number {
  if (typeof value === "number" || typeof value === "string") return value;
  return value === null || value === undefined ? "" : JSON.stringify(value);
}

function primitive(value: unknown): string | number | undefined {
  return typeof value === "string" || typeof value === "number"
    ? value
    : undefined;
}

function singular(label: string): string {
  return label.endsWith("s") ? label.slice(0, -1) : label;
}

function message(reason: unknown): string {
  return reason instanceof Error ? reason.message : "Request failed";
}
